import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Input,
  Button,
  FormField,
  SpaceBetween,
  Form,
  Container,
  InputProps,
  Textarea,
  ColumnLayout,
  Select,
  Header,
  Multiselect,
  SelectProps,
  Modal,
  Box,
} from "@cloudscape-design/components-themed";

import Table from "./Table";
import SearchBar from "./SearchBar";
import { clearSearchCondition } from "../../features/slices/company-search";
import { showModal } from "../UI/modal/modal-slice";
import { useAppDispatch, useAppSelector } from "../../app/hook";
import { getErrorText, isMailValid, isNull } from "../../utils/inputValidation";
import { COMMON_LABELS } from "../default-constants";
import { LABELS, getCompanyTypeLabel, SELECT_BY_COMPANY } from "./constants";
import {
  UserCreateRequest,
  UserUpdateRequest,
  CompanyNamesSearchResponse,
  ReferenceGroupNamesSearchRequest,
} from "../../features/api-interfaces";
import {
  useUpdateUserMutation,
  useAddUserMutation,
  useGetReferenceGroupNamesQuery,
  useGetRolesQuery,
  useGetUsersQuery,
  useUpdateUserReissueInitialPasswordMutation,
  useGetAuthQuery,
  useGetHoldingsUpdateInfoQuery,
} from "../../features/api";
import { TypeData } from "../../features/slices/type-data";
import {
  clearFormFieldErrTxt,
  createFormFieldErrTxts,
  clearAllErrTxts,
} from "../../features/error/validation-slice";
import { getCodeLabel } from "../../utils/common";
import { USER_TYPE, USER_TYPE_MER_HD } from "../../configs/constants";
import { useCanIShowHook } from "../../app/useCanIShowHook";
import { useCodelistHook } from "../../app/useCodelistHook";

interface UserDetailInput {
  cognitoId: string;
  companyType: string;
  companyId: string;
  familyName: string;
  givenName: string;
  email: string;
  memo: string;
  comment: string;
  companyName: string;
  roleId: number | string;
  userReferenceGroups: string[];
}

//(MoBills）許容文字種別 正規表現
const forbiddenCharactersPattern =
  /[纊褜鍈銈蓜俉炻昱棈鋹曻彅丨仡仼伀伃伹佖侒侊侚侔俍偀倢俿倞偆偰偂傔僴僘兊兤冝冾凬刕劜劦勀勛匀匇匤卲厓厲叝﨎咜咊咩哿喆坙坥垬埈埇﨏塚增墲夋奓奛奝奣妤妺孖寀甯寘寬尞岦岺峵崧嵓﨑嵂嵭嶸嶹巐弡弴彧德忞恝悅悊惞惕愠惲愑愷愰憘戓抦揵摠撝擎敎昀昕昻昉昮昞昤晥晗晙晴晳暙暠暲暿曺朎朗杦枻桒柀栁桄棏﨓楨﨔榘槢樰橫橆橳橾櫢櫤毖氿汜沆汯泚洄涇浯涖涬淏淸淲淼渹湜渧渼溿澈澵濵瀅瀇瀨炅炫焏焄煜煆煇凞燁燾犱犾猤猪獷玽珉珖珣珒琇珵琦琪琩琮瑢璉璟甁畯皂皜皞皛皦益睆劯砡硎硤硺礰礼神祥禔福禛竑竧靖竫箞精絈絜綷綠緖繒罇羡羽茁荢荿菇菶葈蒴蕓蕙蕫﨟薰蘒﨡蠇裵訒訷詹誧誾諟諸諶譓譿賰賴贒赶﨣軏﨤逸遧郞都鄕鄧釚釗釞釭釮釤釥鈆鈐鈊鈺鉀鈼鉎鉙鉑鈹鉧銧鉷鉸鋧鋗鋙鋐﨧鋕鋠鋓錥錡鋻﨨錞鋿錝錂鍰鍗鎤鏆鏞鏸鐱鑅鑈閒隆﨩隝隯霳霻靃靍靏靑靕顗顥飯飼餧館馞驎髙髜魵魲鮏鮱鮻鰀鵰鵫鶴鸙黑ⅰⅱⅲⅳⅴⅵⅶⅷⅸⅹ￢￤＇＂㈱№㏍℡①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳ⅠⅡⅢⅣⅤⅥⅦⅧⅨⅩ㍉㌔㌢㍍㌘㌧㌃㌶㍑㍗㌍㌦㌣㌫㍊㌻㎜㎝㎞㎎㎏㏄㎡㍻〝〟№㏍℡㊤㊥㊦㊧㊨㈱㈲㈹㍾㍽㍼≒≡∫∮∑√⊥┴∠∟⊿∵∩∪｡｢｣､･ｦｧｨｩｪｫｬｭｮｯｰｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜﾝﾞﾟ│┌┐┘└├┬┤┴┼━┃┏┓┛┗┣┳┫┻╋┠┯┨┷┿┝┰┥┸╂￢─]/;

const User = (): JSX.Element => {
  // ユーザ種別
  const isDocomoUser = useCanIShowHook(USER_TYPE.DOCOMO);

  // Get the userId param from the URL.
  const { cognitoId } = useParams();

  const navigator = useNavigate();
  const [updateUser] = useUpdateUserMutation();
  const [addUser] = useAddUserMutation();
  const [reissueInitialPass] = useUpdateUserReissueInitialPasswordMutation();
  const { loginCompanyType, loginCompanyCode } = useGetAuthQuery(undefined, {
    selectFromResult: ({ data }) => ({
      loginCompanyType: data?.companyType,
      loginCompanyCode: data?.companyCode,
    }),
  });

  const dispatch = useAppDispatch();
  const { formFieldErrTxts } = useAppSelector((state) => state.validation);

  const isCreateMode = cognitoId === "signup";
  const companyUserCodes = useMemo(
    () => [
      USER_TYPE.MERCHANT_GEN,
      USER_TYPE.MERCHANT_INC,
      USER_TYPE.HD,
      USER_TYPE.GW,
    ],
    []
  );

  // 事業者種別に紐づくロール情報取得API呼び出し
  const { data: rolesRowData } = useGetRolesQuery();
  const { companyType } = useCodelistHook();

  /**
   * ユーザ種別と権限のリストを作成
   * companyTypeAndRoles: ユーザ種別選択肢
   */
  const companyTypeAndRoles: TypeData[] = useMemo(() => {
    let typeData: TypeData[] = [];
    if (rolesRowData && companyType) {
      typeData = companyType
        .filter((code: any) => code.value !== "05")
        .map((code: any) => {
          const companyTypeRole =
            rolesRowData[
              rolesRowData.findIndex((i) => i.companyTypeCode === code.value)
            ];
          return {
            label: code.label,
            value: code.value,
            permissions: companyTypeRole.roles,
          };
        });
    }
    return typeData;
  }, [rolesRowData, companyType]);

  // 権限選択肢
  const [roleList, setRoleList] = useState<any[]>([]);

  // 未ログイン
  const [nonLogin, setNonLogin] = useState<boolean>(false);

  // ユーザ情報取得(更新時)
  const { searchCriteria } = useAppSelector((state) => state.userList);
  const {
    typeCode,
    permissionCode,
    email,
    name,
    deactivateFlag,
    memo,
    comment,
  } = searchCriteria;
  const { data: usersSearchResultData } = useGetUsersQuery(searchCriteria, {
    skip: !(
      typeCode ||
      permissionCode ||
      email ||
      name ||
      deactivateFlag ||
      memo ||
      comment
    ),
  });
  const userDetail: UserDetailInput = useMemo(() => {
    let userDetailTmp = {
      cognitoId: "",
      companyType: "",
      companyId: "",
      roleId: "",
      userReferenceGroups: [],
      email: "",
      familyName: "",
      givenName: "",
      memo: "",
      comment: "",
      companyName: "",
    } as UserDetailInput;
    if (cognitoId) {
      const userDetail = usersSearchResultData?.find(
        (e) => e.cognitoId === cognitoId
      );

      if (userDetail) {
        setNonLogin(!userDetail.authedAt);

        userDetailTmp = {
          cognitoId: userDetail.cognitoId,
          companyType: userDetail.companyType,
          companyId: userDetail.companyId,
          familyName: userDetail.familyName,
          givenName: userDetail.givenName,
          email: userDetail.email,
          memo: userDetail.memo,
          comment: userDetail.comment,
          companyName: userDetail.companyName,
          roleId: userDetail.roleId,
          userReferenceGroups: userDetail.userReferenceGroups,
        } as UserDetailInput;
      }
    }
    return userDetailTmp;
  }, [cognitoId, usersSearchResultData]);

  //ユーザ入力
  const [input, setInput] = useState<UserDetailInput>(userDetail);

  const [companyUserFlag, setCompanyUserFlag] = useState<boolean>(
    companyUserCodes.includes(input.companyType)
  );

  // ホールディングスの情報取得(HDユーザー)
  const fetchCompanyCode = isCreateMode
    ? loginCompanyCode
    : userDetail.companyId;
  const fetchCompanyType = isCreateMode
    ? loginCompanyType
    : userDetail.companyType;

  const shouldFetch = fetchCompanyType === USER_TYPE.HD && !!fetchCompanyCode;

  const { data: holdingCompanyData } = useGetHoldingsUpdateInfoQuery(
    fetchCompanyCode,

    {
      skip: !shouldFetch,
    }
  );

  // 所属組織選択
  const [companyData, setCompanyData] = useState<CompanyNamesSearchResponse>({
    companyId: userDetail.companyId,
    companyName: userDetail.companyName,
    hdReferenceGroupType: holdingCompanyData?.referenceGroupType ?? "",
  });

  useEffect(() => {
    if (holdingCompanyData && userDetail.companyType === USER_TYPE.HD) {
      setCompanyData((prev) => ({
        ...prev,
        hdReferenceGroupType: holdingCompanyData.referenceGroupType,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdingCompanyData, companyData.companyId]);

  // 参照グループ検索リクエストパラメータ
  const [
    referenceGroupNamesSearchRequest,
    setReferenceGroupNamesSearchRequest,
  ] = useState<ReferenceGroupNamesSearchRequest>({
    applyCompanyId: "",
    companyType: "",
  });
  useEffect(() => {
    setReferenceGroupNamesSearchRequest((prev) => ({
      applyCompanyId: companyData.companyId,
      companyType: input.companyType,
    }));
  }, [input.companyType, companyData.companyId]);

  // 参照範囲選択肢
  const { referenceGroupList } = useGetReferenceGroupNamesQuery(
    referenceGroupNamesSearchRequest,
    {
      skip:
        (input.companyType !== USER_TYPE.MERCHANT_GEN &&
          input.companyType !== USER_TYPE.MERCHANT_INC &&
          input.companyType !== USER_TYPE.HD) ||
        !referenceGroupNamesSearchRequest.applyCompanyId,
      selectFromResult: ({ data }) => ({
        referenceGroupList:
          data?.map(
            (ref) =>
              ({
                label: ref.referenceGroupName,
                value: ref.referenceGroupId,
              } as SelectProps.Option)
          ) ?? ([] as SelectProps.Options),
      }),
    }
  );

  /**
   * 個別入力チェック
   */
  const validationCheck = useCallback(
    (
      name: keyof UserDetailInput,
      value: string | number | string[]
    ): boolean => {
      switch (name) {
        case "companyType":
          if (isNull(value as string)) {
            dispatch(
              createFormFieldErrTxts({
                name: name,
                msgId: "KGRERR023",
                msgArgs: [LABELS.TYPE],
              })
            );
            return false;
          } else {
            dispatch(clearFormFieldErrTxt(name));
            return true;
          }
        case "companyId":
          if (isNull(value as string)) {
            dispatch(
              createFormFieldErrTxts({
                name: name,
                msgId: "KGRERR023",
                msgArgs: [LABELS.COMPANY_NAME],
              })
            );
            return false;
          } else {
            dispatch(clearFormFieldErrTxt(name));
            return true;
          }
        case "email":
          if (isNull(value as string)) {
            dispatch(
              createFormFieldErrTxts({
                name: name,
                msgId: "KGRERR023",
                msgArgs: [LABELS.EMAIL],
              })
            );
            return false;
          } else if (!isMailValid(value as string)) {
            dispatch(
              createFormFieldErrTxts({
                name: name,
                msgId: "KGRERR025",
                msgArgs: [],
              })
            );
            return false;
          } else if ((value as string).length > 100) {
            dispatch(
              createFormFieldErrTxts({
                name: name,
                msgId: "KGRERR024",
                msgArgs: ["メールアドレス", "100"],
              })
            );
            return false;
          } else {
            dispatch(clearFormFieldErrTxt(name));
            return true;
          }
        case "familyName":
          if (!value) {
            dispatch(
              createFormFieldErrTxts({
                name: name,
                msgId: "KGRERR023",
                msgArgs: [LABELS.FAMILY_NAME],
              })
            );
            return false;
          } else if ((value as string).length > 15) {
            dispatch(
              createFormFieldErrTxts({
                name: name,
                msgId: "KGRERR024",
                msgArgs: ["ユーザ氏名（姓）", "15"],
              })
            );
            return false;
          } else if (forbiddenCharactersPattern.test(value as string)) {
            dispatch(
              createFormFieldErrTxts({
                name: name,
                msgId: "KGRERR031",
                msgArgs: [LABELS.FAMILY_NAME],
              })
            );
            return false;
          } else {
            dispatch(clearFormFieldErrTxt(name));
            return true;
          }
        case "givenName":
          if (!value) {
            dispatch(
              createFormFieldErrTxts({
                name: name,
                msgId: "KGRERR023",
                msgArgs: [LABELS.GIVEN_NAME],
              })
            );
            return false;
          } else if ((value as string).length > 15) {
            dispatch(
              createFormFieldErrTxts({
                name: name,
                msgId: "KGRERR024",
                msgArgs: ["ユーザ氏名（名）", "15"],
              })
            );
            return false;
          } else if (forbiddenCharactersPattern.test(value as string)) {
            dispatch(
              createFormFieldErrTxts({
                name: name,
                msgId: "KGRERR031",
                msgArgs: [LABELS.GIVEN_NAME],
              })
            );
            return false;
          } else {
            dispatch(clearFormFieldErrTxt(name));
            return true;
          }
        case "memo":
          if (!isNull(value as string) && (value as string).length > 2000) {
            dispatch(
              createFormFieldErrTxts({
                name: name,
                msgId: "KGRERR024",
                msgArgs: ["備考", "2000"],
              })
            );
            return false;
          } else if (
            !isNull(value as string) &&
            forbiddenCharactersPattern.test(value as string)
          ) {
            dispatch(
              createFormFieldErrTxts({
                name: name,
                msgId: "KGRERR031",
                msgArgs: ["備考"],
              })
            );
            return false;
          } else {
            dispatch(clearFormFieldErrTxt(name));
            return true;
          }
        case "comment":
          if (!isNull(value as string) && (value as string).length > 2000) {
            dispatch(
              createFormFieldErrTxts({
                name: name,
                msgId: "KGRERR024",
                msgArgs: ["内部コメント", "2000"],
              })
            );
            return false;
          } else if (
            !isNull(value as string) &&
            forbiddenCharactersPattern.test(value as string)
          ) {
            dispatch(
              createFormFieldErrTxts({
                name: name,
                msgId: "KGRERR031",
                msgArgs: ["内部コメント"],
              })
            );
            return false;
          } else {
            dispatch(clearFormFieldErrTxt(name));
            return true;
          }
        case "roleId":
          if (!value) {
            dispatch(
              createFormFieldErrTxts({
                name: name,
                msgId: "KGRERR023",
                msgArgs: [LABELS.PRIVILEGE],
              })
            );
            return false;
          } else {
            dispatch(clearFormFieldErrTxt(name));
            return true;
          }
        case "userReferenceGroups":
          if (
            input.companyType === USER_TYPE.MERCHANT_GEN ||
            input.companyType === USER_TYPE.MERCHANT_INC
          ) {
            if ((value as string[]).length === 0) {
              dispatch(
                createFormFieldErrTxts({
                  name: name,
                  msgId: "KGRERR023",
                  msgArgs: [LABELS.REFERENCE_GROUP],
                })
              );
              return false;
            } else {
              dispatch(clearFormFieldErrTxt(name));
              return true;
            }
          }
          dispatch(clearFormFieldErrTxt(name));
          return true;
        default:
          return false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, input]
  );
  // 入力更新関数
  const handleInputOnChange = useCallback(
    (name: keyof UserDetailInput, value: string | string[] | number) => {
      let resetInput = { ...input, [name]: value };
      if ((name === "companyType" || name === "companyId") && isDocomoUser) {
        if (name === "companyType") {
          setCompanyUserFlag(companyUserCodes.includes(value as string));
          resetInput.companyId = "";
          setCompanyData({
            companyId: "",
            companyName: "",
            hdReferenceGroupType: "",
          });
          const roles = companyTypeAndRoles.filter((p) => p.value === value)[0]
            .permissions;
          setRoleList(roles);
        }
        resetInput.userReferenceGroups = [];
        resetInput.roleId = "";
      }

      setInput(resetInput);

      validationCheck(name, value);
    },
    [
      companyTypeAndRoles,
      companyUserCodes,
      input,
      validationCheck,
      isDocomoUser,
    ]
  );

  /**
   * ユーザ種別入力関数
   */
  const handleCompanyTypeOnChange = ({ detail }: { detail: any }) => {
    const selected = detail.selectedOption;
    handleInputOnChange("companyType", selected.value);
  };

  const [visible, setVisible] = useState(false);
  const [isSkipSearch, setIsSkipSearch] = useState(true);
  const [selectedItems, setSelectedItems] = useState<
    CompanyNamesSearchResponse[]
  >([]);

  /**
   * 検索モーダルの決定押下時
   */
  const searchModalPositiveFunc = () => {
    if (0 < selectedItems.length) {
      const companyNameData = selectedItems[0];
      setCompanyData(companyNameData);
      handleInputOnChange("companyId", companyNameData.companyId);
    }
    searchModalNegativeFunc();
  };

  /**
   * 検索モーダルのキャンセル押下時
   */
  const searchModalNegativeFunc = () => {
    setIsSkipSearch(true);
    dispatch(clearSearchCondition());
    dispatch(clearFormFieldErrTxt("companyName"));
    setVisible(false);
  };

  /**
   * 所属組織検索ボタン押下時
   */
  const handleCompanyNameSearchBtnOnClick = () => {
    if (companyData.companyId !== "") {
      setSelectedItems([companyData]);
    }
    setVisible(companyUserCodes.includes(input.companyType));
  };

  /**
   * 参照グループ入力関数
   */
  const handleReferenceGroupOnChange = ({ detail }: { detail: any }) => {
    const selectedOptionsTmp = detail.selectedOptions;
    const selectedReferenceGroups = selectedOptionsTmp.map(
      (option: any) => option.value
    );
    handleInputOnChange("userReferenceGroups", selectedReferenceGroups);
  };

  /**
   * 権限入力関数
   */
  const handleRoleOnChange = ({ detail }: { detail: any }) => {
    handleInputOnChange("roleId", detail.selectedOption.value);
  };

  /**
   * 全項目の入力チェック
   */
  const allValidationCheck = () => {
    const companyTypeCheckResult = validationCheck(
      "companyType",
      input.companyType
    );
    const emailCheckResult = validationCheck("email", input.email);
    const familyNameCheckResult = validationCheck(
      "familyName",
      input.familyName
    );
    const givenNameCheckResult = validationCheck("givenName", input.givenName);
    const memoCheckResult = validationCheck("memo", input.memo);
    const commentCheckResult = validationCheck("comment", input.memo);
    const userRoleCheckResult = validationCheck("roleId", input.roleId);
    const referenceGroupCheckResult = validationCheck(
      "userReferenceGroups",
      input.userReferenceGroups
    );
    const companyIdCheckResult = companyUserFlag
      ? validationCheck("companyId", input.companyId)
      : true;
    return (
      companyTypeCheckResult &&
      emailCheckResult &&
      familyNameCheckResult &&
      givenNameCheckResult &&
      memoCheckResult &&
      commentCheckResult &&
      userRoleCheckResult &&
      companyIdCheckResult &&
      referenceGroupCheckResult
    );
  };

  //戻るBTN
  const handleBackBtnOnClick = () => navigator("/users?isBackFromDetail=true");

  /**
   * 登録or更新実行関数
   */
  const executeFunction = (regist: boolean) => {
    if (allValidationCheck()) {
      if (regist) {
        // 登録時にcognitoId、companyNameを除く
        let payload = {
          companyType: input.companyType,
          companyId: input.companyId,
          email: input.email,
          familyName: input.familyName,
          givenName: input.givenName,
          memo: input.memo,
          comment: input.comment,
          roleId: input.roleId,
          userReferenceGroups: input.userReferenceGroups,
        } as UserCreateRequest;

        addUser(payload).then((res: any) => {
          if (!("error" in res) && "error" !== res.data.status) {
            navigator("/users");
          }
        });
      } else {
        // 更新時にcompanyName、companyId、companyTypeを除く
        let payload = {
          cognitoId: cognitoId,
          email: input.email,
          familyName: input.familyName,
          givenName: input.givenName,
          memo: input.memo,
          comment: input.comment,
          roleId: input.roleId,
          userReferenceGroups: input.userReferenceGroups,
        } as UserUpdateRequest;
        updateUser(payload).then((res: any) => {
          if (!("error" in res) && "error" !== res.data.status) {
            navigator("/users");
          }
        });
      }
    }
  };

  //登録更新関数
  const handleOnSubmitClick = () => {
    let modalContent: string;
    let modalFunction: VoidFunction;
    if (isCreateMode) {
      modalContent = COMMON_LABELS.CONTENTS.REGISTER_CONFIRM;
      modalFunction = () => executeFunction(true);
    } else {
      modalContent = COMMON_LABELS.CONTENTS.UPDATE_CONFIRM;
      modalFunction = () => executeFunction(false);
    }
    dispatch(
      showModal({
        title: COMMON_LABELS.TITLES.DL,
        content: modalContent,
        positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
        negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
        positiveFn: modalFunction,
      })
    );
  };

  // 仮パスワード再発行関数
  const reissueFunc = () => {
    if (cognitoId) {
      reissueInitialPass(cognitoId).catch(() => {});
    }
  };

  // 仮パスワード再発行
  const handleReissuePassOnClick = () => {
    dispatch(
      showModal({
        title: COMMON_LABELS.TITLES.DL,
        content: LABELS.REISSUE_MESSAGE,
        positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
        negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
        positiveFn: reissueFunc,
      })
    );
  };

  /** ロールリスト設定 */
  useEffect(() => {
    if ((input.companyType || !isDocomoUser) && companyTypeAndRoles) {
      const roles = companyTypeAndRoles.filter(
        (p) => p.value === (input.companyType || loginCompanyType)
      )[0]?.permissions;
      setRoleList(roles);
    }
  }, [input.companyType, isDocomoUser, companyTypeAndRoles, loginCompanyType]);

  /**
   * アンマウント時にエラー、検索条件全クリア
   */
  useEffect(() => {
    return () => {
      dispatch(clearAllErrTxts());
    };
  }, [dispatch]);

  /**
   * ドコモサイト以外の場合、予めユーザ種別、事業者コード、参照範囲設定
   */
  useEffect(() => {
    if (!isDocomoUser && loginCompanyType && loginCompanyCode) {
      let resetInput = {
        ...input,
        companyType: loginCompanyType,
        companyId: loginCompanyCode,
      };
      if (USER_TYPE_MER_HD.includes(loginCompanyType)) {
        setCompanyData({
          companyId: loginCompanyCode,
          companyName: "",
          hdReferenceGroupType: holdingCompanyData?.referenceGroupType ?? "",
        });
      }
      setInput(resetInput);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDocomoUser, loginCompanyType, loginCompanyCode, input]);

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="m">
            <Button
              formAction="none"
              variant="link"
              onClick={handleBackBtnOnClick}
            >
              {COMMON_LABELS.BTNS.BACK}
            </Button>
            <Button variant="primary" onClick={handleOnSubmitClick}>
              {isCreateMode
                ? COMMON_LABELS.BTNS.REGISTER
                : COMMON_LABELS.BTNS.UPDATE}
            </Button>
          </SpaceBetween>
        }
      >
        <Container header={<Header>{LABELS.USER_INFO}</Header>}>
          <SpaceBetween direction="vertical" size="xs">
            {/* ドコモユーザのみ */}
            {isDocomoUser && (
              <FormField
                label={LABELS.TYPE}
                errorText={getErrorText("companyType", formFieldErrTxts)}
              >
                {isCreateMode ? (
                  <Select
                    selectedOption={{
                      value: String(input.companyType),
                      label: getCodeLabel(
                        companyTypeAndRoles,
                        String(input.companyType)
                      ),
                    }}
                    onChange={handleCompanyTypeOnChange}
                    options={companyTypeAndRoles}
                    placeholder={LABELS.PLACEHOLDER.TYPE}
                  />
                ) : (
                  getCodeLabel(companyTypeAndRoles, String(input.companyType))
                )}
              </FormField>
            )}

            {companyUserFlag && isDocomoUser && (
              <FormField
                label={getCompanyTypeLabel(input.companyType)}
                errorText={getErrorText("companyId", formFieldErrTxts)}
              >
                {isCreateMode ? (
                  <SpaceBetween direction="horizontal" size="xs">
                    <Input
                      value={companyData.companyName}
                      disabled={!companyData.companyName}
                      readOnly={!!companyData.companyName}
                    />
                    <Button onClick={handleCompanyNameSearchBtnOnClick}>
                      {LABELS.SEARCH_MODAL_TITLE}
                    </Button>
                    <Modal
                      visible={visible}
                      onDismiss={searchModalNegativeFunc}
                      closeAriaLabel={"Close modal"}
                      size={"large"}
                      footer={
                        <Box float={"right"}>
                          <SpaceBetween direction={"horizontal"} size={"xs"}>
                            <Button
                              variant={"normal"}
                              onClick={searchModalNegativeFunc}
                            >
                              {COMMON_LABELS.NEGATIVE_BUTTON.CANCEL}
                            </Button>
                            <Button
                              variant={"primary"}
                              onClick={searchModalPositiveFunc}
                            >
                              {COMMON_LABELS.POSITIVE_BUTTON.DECIDE}
                            </Button>
                          </SpaceBetween>
                        </Box>
                      }
                      header={LABELS.SEARCH_MODAL_TITLE}
                    >
                      <SpaceBetween size={"xs"} direction={"vertical"}>
                        <SearchBar
                          companyType={input.companyType}
                          setIsSkipSearch={setIsSkipSearch}
                          setSelectedItems={setSelectedItems}
                        />
                        <Table
                          selectedItems={selectedItems}
                          setSelectedItems={setSelectedItems}
                          isSkipSearch={isSkipSearch}
                        />
                      </SpaceBetween>
                    </Modal>
                  </SpaceBetween>
                ) : (
                  input.companyName
                )}
              </FormField>
            )}

            <FormField
              label={LABELS.PRIVILEGE}
              errorText={getErrorText("roleId", formFieldErrTxts)}
            >
              <Select
                selectedOption={{
                  value: String(input.roleId),
                  label: getCodeLabel(roleList, String(input.roleId)),
                }}
                onChange={handleRoleOnChange}
                options={roleList}
                placeholder={LABELS.PLACEHOLDER.PRIVILEGE}
              />
            </FormField>

            {(input.companyType === USER_TYPE.MERCHANT_GEN ||
              input.companyType === USER_TYPE.MERCHANT_INC ||
              (input.companyType === USER_TYPE.HD &&
                companyData.hdReferenceGroupType === SELECT_BY_COMPANY)) &&
              input.companyId && (
                <FormField
                  label={LABELS.REFERENCE_GROUP}
                  errorText={getErrorText(
                    "userReferenceGroups",
                    formFieldErrTxts
                  )}
                >
                  <Multiselect
                    selectedOptions={referenceGroupList.filter((ref: any) =>
                      input.userReferenceGroups.includes(ref.value)
                    )}
                    onChange={handleReferenceGroupOnChange}
                    options={referenceGroupList}
                    placeholder={LABELS.PLACEHOLDER.REFERENCE_GROUP}
                  />
                </FormField>
              )}

            <FormField
              label={LABELS.EMAIL}
              errorText={getErrorText("email", formFieldErrTxts)}
            >
              <Input
                name="email"
                value={input.email}
                onChange={({ detail }: { detail: InputProps.ChangeDetail }) =>
                  handleInputOnChange("email", detail.value)
                }
              />
            </FormField>
            <ColumnLayout columns={3}>
              <FormField
                label={LABELS.FAMILY_NAME}
                errorText={getErrorText("familyName", formFieldErrTxts)}
              >
                <Input
                  name="familyName"
                  value={input.familyName}
                  onChange={({ detail }: { detail: InputProps.ChangeDetail }) =>
                    handleInputOnChange("familyName", detail.value)
                  }
                />
              </FormField>
              <FormField
                label={LABELS.GIVEN_NAME}
                errorText={getErrorText("givenName", formFieldErrTxts)}
              >
                <Input
                  name="givenName"
                  value={input.givenName}
                  onChange={({ detail }: { detail: InputProps.ChangeDetail }) =>
                    handleInputOnChange("givenName", detail.value)
                  }
                />
              </FormField>
            </ColumnLayout>
            <FormField
              label={LABELS.MEMO}
              errorText={getErrorText("memo", formFieldErrTxts)}
            >
              <Textarea
                name="memo"
                value={input.memo}
                onChange={({ detail }: { detail: InputProps.ChangeDetail }) =>
                  handleInputOnChange("memo", detail.value)
                }
              />
            </FormField>
            {/* ドコモユーザのみ */}
            {isDocomoUser && (
              <FormField
                label={LABELS.COMMENT}
                errorText={getErrorText("comment", formFieldErrTxts)}
              >
                <Textarea
                  name="comment"
                  value={input.comment}
                  onChange={({ detail }: { detail: InputProps.ChangeDetail }) =>
                    handleInputOnChange("comment", detail.value)
                  }
                />
              </FormField>
            )}

            {nonLogin && (
              <Button onClick={handleReissuePassOnClick}>
                {LABELS.REISSUE}
              </Button>
            )}
          </SpaceBetween>
        </Container>
      </Form>
    </form>
  );
};

export default User;
